import React, { useLayoutEffect, useRef } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import './MarqueeBackground.css';
import { shouldRenderVideoEmbed } from '../../config/videoEmbeds';
import filmstripSrc from '../../assets/videos/header/home-header-filmstrip.mp4';

/**
 * Horizontal marquee of looping header clips behind the Home hero wordmark.
 * Uses a single pre-combined filmstrip asset and two video elements for a seamless loop.
 * Exports:
 * - default: React component; renders nothing when video embeds are disabled in config.
 */
const MarqueeBackground = () => {
  const controls = useAnimationControls();
  const trackRef = useRef(null);
  const showVideos = shouldRenderVideoEmbed('marqueeBackground');

  useLayoutEffect(() => {
    if (!showVideos) {
      return undefined;
    }

    const runMarquee = () => {
      const trackEl = trackRef.current;
      if (!trackEl) {
        return;
      }
      const wraps = trackEl.querySelectorAll('.marquee-filmstrip-wrap');
      if (wraps.length < 2) {
        return;
      }
      const r0 = wraps[0].getBoundingClientRect();
      const r1 = wraps[1].getBoundingClientRect();
      const gapPx = Math.max(0, r1.left - r0.right);
      const travel = r0.width + gapPx;
      controls.start({
        x: [0, -travel],
        transition: {
          duration: 30,
          repeat: Infinity,
          ease: 'linear',
        },
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(runMarquee));
    window.addEventListener('resize', runMarquee);
    return () => window.removeEventListener('resize', runMarquee);
  }, [controls, showVideos]);

  if (!showVideos) {
    return null;
  }

  return (
    <div className="marquee-background">
      <motion.div
        ref={trackRef}
        className="marquee-track"
        animate={controls}
      >
        <div className="marquee-filmstrip-wrap">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="marquee-filmstrip"
          >
            <source src={filmstripSrc} type="video/mp4" />
          </video>
        </div>
        <div className="marquee-filmstrip-wrap">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="marquee-filmstrip"
          >
            <source src={filmstripSrc} type="video/mp4" />
          </video>
        </div>
      </motion.div>
    </div>
  );
};

export default MarqueeBackground;
